import React, { ChangeEvent } from 'react'
import { Link, Checkbox } from '@elamajs/ui-kit'
import SelectWithSearch from '../SelectWithSearch'
import DatePicker from '../DatePicker'
import Tooltip from '../Tooltip'

import * as Styled from './FilterInputs.styled'

type ThematicsSelectProps = {
  isDisabled: boolean
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string, label: string) => void
  label: string
  onTooltipLinkClick: () => void
  isAnalytics?: boolean
}

const ThematicsSelect: React.FC<ThematicsSelectProps> = ({
  value,
  options,
  isDisabled,
  onChange,
  label,
  onTooltipLinkClick,
  isAnalytics
}) => {
  return (
    <Styled.Row>
      {isAnalytics ? (
        <Styled.Label>Тематика канала</Styled.Label>
      ) : (
        <Styled.Label>
          Тематика бизнеса&nbsp;
          <Styled.Tooltip as={Tooltip} width="300px" direction="bottom">
            <span>
              Какие направления бизнеса и&nbsp;каналы относятся к&nbsp;каждой тематике, смотрите
              <br />
              в&nbsp;классификаторе
            </span>
            <Styled.Link as={Link} onClick={onTooltipLinkClick}>
              Классификатор тематик
            </Styled.Link>
          </Styled.Tooltip>
        </Styled.Label>
      )}
      <SelectWithSearch
        disabled={isDisabled}
        options={options}
        value={value}
        onChange={onChange}
        label={label}
      />
    </Styled.Row>
  )
}

type SelectProps = {
  isDisabled: boolean
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string, label: string) => void
  title: React.ReactNode
  label: string
}

const Select: React.FC<SelectProps> = ({ value, options, isDisabled, onChange, title, label }) => {
  return (
    <Styled.Row>
      <Styled.Label>{title}</Styled.Label>
      <SelectWithSearch
        disabled={isDisabled}
        options={options}
        value={value}
        label={label}
        onChange={onChange}
      />
    </Styled.Row>
  )
}

type CheckboxGroupProps = {
  isDisabled: boolean
  isCheckboxChecked: (name: string, value: string) => boolean
  onChange: (checked: boolean, e: ChangeEvent<HTMLInputElement>) => void
}

const PlacementCheckboxGroup: React.FC<CheckboxGroupProps> = ({
  isDisabled,
  isCheckboxChecked,
  onChange
}) => {
  return (
    <Styled.Row>
      <Styled.Label>Посадка</Styled.Label>
      <Styled.CheckboxGroup>
        <Styled.Checkbox
          as={Checkbox}
          label="Каналы"
          name="posadka"
          value="channel"
          checked={isCheckboxChecked('posadka', 'channel')}
          disabled={isDisabled}
          onChange={onChange}
        />
        <Styled.Checkbox
          as={Checkbox}
          label="Бот"
          name="posadka"
          value="bot"
          checked={isCheckboxChecked('posadka', 'bot')}
          disabled={isDisabled}
          onChange={onChange}
        />
        <Styled.Checkbox
          as={Checkbox}
          label="Сайт"
          name="posadka"
          value="site"
          checked={isCheckboxChecked('posadka', 'site')}
          disabled={isDisabled}
          onChange={onChange}
        />
      </Styled.CheckboxGroup>
    </Styled.Row>
  )
}

const TargetingCheckboxGroup: React.FC<CheckboxGroupProps> = ({
  isDisabled,
  isCheckboxChecked,
  onChange
}) => {
  return (
    <Styled.Row>
      <Styled.Label>Тип таргетинга</Styled.Label>
      <Styled.CheckboxGroup>
        <Styled.Checkbox
          as={Checkbox}
          label="Каналы"
          name="targeting"
          value="channel"
          checked={isCheckboxChecked('targeting', 'channel')}
          disabled={isDisabled}
          onChange={onChange}
        />
        <Styled.Checkbox
          as={Checkbox}
          label="Тематика каналов"
          name="targeting"
          value="category"
          checked={isCheckboxChecked('targeting', 'category')}
          disabled={isDisabled}
          onChange={onChange}
        />
        <Styled.Checkbox
          as={Checkbox}
          label="По пользователю"
          name="targeting"
          value="user"
          checked={isCheckboxChecked('targeting', 'user')}
          disabled={isDisabled}
          onChange={onChange}
        />
      </Styled.CheckboxGroup>
    </Styled.Row>
  )
}

const CreativeCheckboxGroup: React.FC<CheckboxGroupProps> = ({
  isDisabled,
  isCheckboxChecked,
  onChange
}) => {
  return (
    <Styled.Row>
      <Styled.Label>
        Формат креатива&nbsp;
        <Styled.Tooltip as={Tooltip} width="233px" direction="top">
          <span>Данные доступны с января 2025</span>
        </Styled.Tooltip>
      </Styled.Label>
      <Styled.CheckboxGroup>
        <Styled.Checkbox
          as={Checkbox}
          label="Текстовое объявление"
          name="creative"
          value="text"
          checked={isCheckboxChecked('creative', 'text')}
          disabled={isDisabled}
          onChange={onChange}
        />
        <Styled.Checkbox
          as={Checkbox}
          label="Картинка или видео"
          name="creative"
          value="media"
          checked={isCheckboxChecked('creative', 'media')}
          disabled={isDisabled}
          onChange={onChange}
        />
      </Styled.CheckboxGroup>
    </Styled.Row>
  )
}

type PeriodDatePickerProps = {
  availableDateRange: { min: Date | undefined; max: Date | undefined }
  value: { min: Date | undefined; max: Date | undefined }
  disabled: boolean
  onChange: (value: { min: Date; max: Date }) => void
  isAnalytics?: boolean
}

const PeriodDatePicker: React.FC<PeriodDatePickerProps> = ({
  availableDateRange,
  value,
  disabled,
  onChange,
  isAnalytics
}) => {
  return (
    <Styled.Row>
      {isAnalytics ? (
        <Styled.Label>
          Период&nbsp;
          <Styled.Tooltip as={Tooltip} width="300px" direction="top-start">
            <span>
              Возможность выбрать от&nbsp;1&nbsp;месяца до&nbsp;максимального кол-ва месяцев.
              По&nbsp;умолчанию при открытии дашборда выбран максимальный период
            </span>
          </Styled.Tooltip>
        </Styled.Label>
      ) : (
        <Styled.Label>Период</Styled.Label>
      )}
      <DatePicker
        availableDateRange={availableDateRange}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
    </Styled.Row>
  )
}

export {
  ThematicsSelect,
  PlacementCheckboxGroup,
  TargetingCheckboxGroup,
  CreativeCheckboxGroup,
  PeriodDatePicker,
  Select
}
